"use client"

import { useEffect, useRef } from "react"

type SparklineProps = {
  w?: number
  h?: number
  color?: string
  count?: number
  className?: string
}

export function Sparkline({ w = 140, h = 60, color = "#8fbfa8", count = 32, className = "st-spark" }: SparklineProps) {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const cnv = ref.current
    if (!cnv) return
    const dpr = window.devicePixelRatio || 1
    cnv.width = w * dpr
    cnv.height = h * dpr
    cnv.style.width = `${w}px`
    cnv.style.height = `${h}px`
    const ctx = cnv.getContext("2d")
    if (!ctx) return
    ctx.scale(dpr, dpr)
    let vals = Array.from({ length: count }, (_, i) => 0.5 + 0.3 * Math.sin(i * 0.4) + Math.random() * 0.18)
    let lastShift = performance.now()
    const draw = () => {
      const now = performance.now()
      if (now - lastShift > 700) {
        vals.shift()
        const last = vals[vals.length - 1] ?? 0.5
        vals.push(Math.max(0.15, Math.min(0.95, last + (Math.random() - 0.45) * 0.15)))
        lastShift = now
      }
      ctx.clearRect(0, 0, w, h)
      ctx.strokeStyle = "rgba(143,191,168,0.06)"
      ctx.lineWidth = 0.5
      for (let i = 0; i < 4; i++) {
        ctx.beginPath()
        ctx.moveTo(0, (i + 0.5) * (h / 4))
        ctx.lineTo(w, (i + 0.5) * (h / 4))
        ctx.stroke()
      }
      ctx.beginPath()
      vals.forEach((v, i) => {
        const x = (i / (count - 1)) * w
        const y = h - v * h * 0.85 - 4
        if (i === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      })
      ctx.strokeStyle = color
      ctx.lineWidth = 1.2
      ctx.shadowBlur = 8
      ctx.shadowColor = color
      ctx.stroke()
      ctx.lineTo(w, h)
      ctx.lineTo(0, h)
      ctx.closePath()
      ctx.fillStyle = `${color}15`
      ctx.fill()
      ctx.shadowBlur = 0
      const lastV = vals[vals.length - 1] ?? 0.5
      const ty = h - lastV * h * 0.85 - 4
      ctx.beginPath()
      ctx.arc(w - 1, ty, 2.5, 0, Math.PI * 2)
      ctx.fillStyle = color
      ctx.fill()
    }
    draw()
    const id = setInterval(draw, 80)
    return () => clearInterval(id)
  }, [w, h, color, count])

  return <canvas ref={ref} className={className} aria-hidden />
}
